// @ts-nocheck
/* eslint-disable consistent-return */
import { getUserInfo } from '@fff-web/fff-utilities';
import { loadSegment } from './utils/loadSegment';
import { getGenericTrackingData } from './utils/genericTracking';
import { getEnv } from './utils/environment';
import {
  analyticsIsNotInitialized,
  ctaNameIsRequired,
  elementParameterIsRequired,
  trackNameIsRequired,
} from './helpers/consoleWarn';
import {
  FffAnalyticsProps,
  FilteredIdentifyProps,
  GetUserInfoProps,
  PropertiesProps,
  TrackElementProps,
  TrackEventProps,
  TrackPageLoadProps,
  TrackProps,
} from '../interfaces';
import { removeTrailingSlash } from './helpers/removeTrailingSlash';
import isBrowser from './utils/isBrowser';
import IdentifyProps from '../interfaces/identifyProps';
import trackInfo from './utils/trackInfo';
import identifyInfo from './utils/identifyInfo';
import { ReferrerQueryString } from './utils/referrerQueryString';
import { SKIP_TITLE_CASE } from './utils/toTitleCase';

let fffAnalytics: FffAnalyticsProps;

if (isBrowser()) {
  // TODO: 'analytics' does not exist on type 'Window & typeof globalThis'
  // @ts-ignore eslint-disable-next-line no-multi-assign
  fffAnalytics = window.fffAnalytics = window.fffAnalytics || {
    segmentToken: getEnv().segmentToken,
    userData: {},
    sessionInfo: getGenericTrackingData(),
    init: false,
  };
}

const initialize = async (): Promise<void> => {
  if (!fffAnalytics) return;
  if (!fffAnalytics.init) fffAnalytics.init = loadSegment({ token: fffAnalytics.segmentToken });
  fffAnalytics.sessionInfo = getGenericTrackingData();
  fffAnalytics.userData = await getUserInfo();
};

const trackPageLoad = async (
  pageLoadProps?: TrackPageLoadProps,
): Promise<void> => {
  const defaultBehavior = {
    pathName: null,
    properties: {},
    options: {},
    callback: () => null,
  };
  const {
    pathName, properties, options, callback,
  } = pageLoadProps || defaultBehavior;
  if (!isBrowser() || !window.analytics) return analyticsIsNotInitialized();
  const localStorageUserInfo: GetUserInfoProps = await getUserInfo();
  fffAnalytics.userData = localStorageUserInfo;
  const page = removeTrailingSlash(
    pathName || isBrowser() ? window.location.pathname : '',
  );
  const trackProperties: PropertiesProps = trackInfo({
    fffAnalytics,
    localStorageUserInfo,
    properties,
  });
  window.analytics.page(page, trackProperties, options, callback);
};

const trackEvent = async ({
  ctaName,
  properties = {},
  options = {},
  callback = () => null,
}: TrackEventProps): Promise<void> => {
  if (!ctaName) return ctaNameIsRequired();
  await track({
    trackName: 'Clicked CTA',
    properties: { cta_name: ctaName, ...properties },
    options,
    callback,
  });
};

const trackElement = async ({
  elementName,
  properties = {},
  options = {},
  callback = () => null,
}: TrackElementProps): Promise<void> => {
  if (!elementName) return elementParameterIsRequired();
  await track({
    trackName: 'Clicked Element',
    properties: { element_name: elementName, ...properties },
    options,
    callback,
  });
};

const track = async ({
  trackName,
  properties = {},
  options = {},
  callback = () => null,
}: TrackProps): Promise<void> => {
  if (!trackName) return trackNameIsRequired();
  if (!isBrowser() || !window.analytics) return analyticsIsNotInitialized();
  const localStorageUserInfo: GetUserInfoProps = await getUserInfo();
  fffAnalytics.userData = localStorageUserInfo;
  const trackProperties: PropertiesProps = trackInfo({
    fffAnalytics,
    localStorageUserInfo,
    properties,
  });
  window.analytics.track(trackName, trackProperties, options, callback);
};

const identify = async (identifyProps?: IdentifyProps): Promise<void> => {
  const defaultIdentifyProps = {
    userId: '',
    traits: {},
    options: {},
    callback: () => null,
  };
  const {
    userId, traits, options, callback,
  } = identifyProps || defaultIdentifyProps;
  if (!isBrowser() || !window.analytics) return analyticsIsNotInitialized();
  const localStorageUserInfo: GetUserInfoProps = await getUserInfo();
  fffAnalytics.userData = localStorageUserInfo;
  const identifyUserId = userId || localStorageUserInfo?.user?.userId;
  const uuid = localStorageUserInfo?.user?.uuid;
  const identifyProperties: FilteredIdentifyProps = identifyInfo({
    userInfo: localStorageUserInfo,
    traits: { fffUUID: uuid, ...traits },
  });
  if (window.analytics.user) window.analytics.user().traits({});
  window.analytics.identify(identifyUserId, identifyProperties, options, callback);
};

export {
  initialize,
  trackPageLoad,
  trackEvent,
  trackElement,
  track,
  identify,
  ReferrerQueryString,
  SKIP_TITLE_CASE,
};
